import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import { selectCodebooks, selectUserLabels } from '../store/selectors';
import { fetchOrgUnits } from '../store/codebooks';
import { fetchUserLabels } from '../store/userLabels';
import { useAppTranslation } from '../services/i18n';
import { CodebookList } from '../components/codebook/CodebookList';
import { UserLabelList } from '../components/userLabel/UserLabelList';
import { WorkplacesEditor } from '../components/workplace/WorkplacesEditor';
import { PageHeader } from '../components/PageHeader';
import { PageTab, PageTabs } from '../components/PageTabs';
import { ListAltRounded } from '@mui/icons-material';

export interface CodebookCategory {
  category: 'department' | 'job_title';
}

// workplaces (department) have their own tree editor, the flat list is kept for job titles
const codebookCategories: CodebookCategory[] = [
  {
    category: 'job_title',
  },
];

export const CodebooksPage: React.FC<{
  isTreeEnabled: boolean;
  isMultipleWorkplacesEnabled: boolean;
}> = ({ isTreeEnabled, isMultipleWorkplacesEnabled }) => {
  const dispatch = useAppDispatch();
  const t = useAppTranslation();
  const codebooks = useSelector(selectCodebooks);
  const userLabels = useSelector(selectUserLabels);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);

  const handleFetchItems = useCallback(() => {
    setIsLoading(true);
    dispatch(fetchOrgUnits()).then(() => {
      setIsLoading(false);
    });
  }, [dispatch]);

  const handleFetchLabels = useCallback(() => {
    setIsLoadingLabels(true);
    dispatch(fetchUserLabels()).then(() => {
      setIsLoadingLabels(false);
    });
  }, [dispatch]);

  useEffect(() => {
    handleFetchItems();
    handleFetchLabels();
  }, [handleFetchItems, handleFetchLabels]);

  const tabs: PageTab[] = [
    {
      tab: 'department',
      label: t('codebooks.department.title'),
      content: () => (
        <div className={'tw-mt-32 workplace-editor__frame'}>
          <WorkplacesEditor
            isTreeEnabled={isTreeEnabled}
            isMultipleWorkplacesEnabled={isMultipleWorkplacesEnabled}
          />
        </div>
      ),
    },
    ...codebookCategories.map((codebookCategory) => ({
      tab: codebookCategory.category,
      label: t('codebooks.' + codebookCategory.category + '.title'),
      content: () => (
        <div className={'tw-mt-32'}>
          <CodebookList
            codebookCategory={codebookCategory}
            itemsState={{ items: codebooks[codebookCategory.category], isLoading }}
            fetchItems={handleFetchItems}
          />
        </div>
      ),
    })),
    {
      tab: 'userLabels',
      label: t('userLabels.title'),
      content: () => (
        <div className={'tw-mt-32'}>
          <UserLabelList
            itemsState={{ items: userLabels.items, isLoading: isLoadingLabels }}
            fetchItems={handleFetchLabels}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={'layout-fill layout-column'}>
      <div className={'codebooks-page kws-content-margin flex tw-flex tw-flex-col'}>
        <PageHeader icon={<ListAltRounded />} title={t('codebooks.pageTitle')} />
        <PageTabs tabs={tabs} />
      </div>
    </div>
  );
};
